'use client';

import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useOverlayStore } from '@/shared/components/overlay/overlay.store';
import { clsx } from 'clsx';
import { X } from 'lucide-react';
import { ModalContext } from './use-modal-context';

export const MODAL_ID = 'overlay-root';

type ModalProps = {
  modalKey: string;
  header?: React.ReactNode;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  onClose?: () => void;
  closeable?: boolean;
};

const Modal = ({
  modalKey,
  header: initialHeader,
  children,
  footer,
  onClose,
  closeable = true,
}: ModalProps) => {
  const { activeModalKey, hideOverlay } = useOverlayStore();
  const [header, setHeader] = useState<React.ReactNode>(initialHeader);

  const handleClose = useCallback(() => {
    if (closeable) {
      hideOverlay();
      onClose?.();
    }
  }, [closeable, hideOverlay, onClose]);

  const isOpen = activeModalKey === modalKey;
  const canRender = typeof document !== 'undefined';

  useEffect(() => {
    if (!isOpen) return;
    const body = document.body;
    const prevOverflow = body.style.overflow;
    body.style.overflow = 'hidden';
    return () => {
      body.style.overflow = prevOverflow;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !closeable) return;

    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen, closeable, handleClose]);

  return canRender && isOpen
    ? createPortal(
        <ModalContext.Provider value={{ setHeader }}>
          <div className='fixed inset-0 z-50 flex items-center justify-center bg-gray-700/90'>
            <div className='bg-background-panel text-text motion-safe:animate-modal-in relative container mx-auto max-h-[90vh] max-w-[900px] overflow-y-auto rounded-lg shadow-xl motion-reduce:animate-none'>
              <div
                className={clsx(
                  'flex justify-between p-4',
                  header && 'border-b border-solid border-white',
                )}
              >
                {header && header}
                {closeable && (
                  <button onClick={handleClose} className='ml-auto pl-4 text-white'>
                    <X size={20} />
                  </button>
                )}
              </div>
              {children}
              {footer && <div className='flex p-4'>{footer}</div>}
            </div>
          </div>
        </ModalContext.Provider>,
        document.getElementById(MODAL_ID)!,
      )
    : null;
};

export default Modal;
